import { useCallback, useEffect, useState } from 'react';
import useHash from './UseHash';
import { ErrorHandler } from '../Services/UserApi';
import InsuranceLicenseService, {
	AddFoodLicensePayload,
	AddLiabilityInsurancePayload,
	InsuranceLicenseData,
} from '../Services/InsuranceLicenseService';

export default function useInsuranceLicense() {
	const [data, setData] = useState<InsuranceLicenseData>();
	const [isLoading, setIsLoading] = useState(true);

	const hash = useHash();

	const loadData = useCallback(async () => {
		setIsLoading(true);
		try {
			const loaded = await InsuranceLicenseService.loadData(hash);
			setData(loaded);
			return loaded;
		} catch (error) {
			ErrorHandler.toast(error);
			return undefined;
		} finally {
			setIsLoading(false);
		}
	}, [hash]);

	const addLiabilityInsurance = useCallback(
		async (payload: AddLiabilityInsurancePayload) => {
			const updatedData = await InsuranceLicenseService.addLiabilityInsurance(hash, payload);
			setData(updatedData);
			return updatedData;
		},
		[hash],
	);

	const removeLiabilityInsurance = useCallback(
		async (policyId: number) => {
			const updatedData = await InsuranceLicenseService.removeLiabilityInsurance(hash, policyId);
			setData(updatedData);
			return updatedData;
		},
		[hash],
	);

	const addFoodLicense = useCallback(
		async (payload: AddFoodLicensePayload) => {
			const updatedData = await InsuranceLicenseService.addFoodLicense(hash, payload);
			setData(updatedData);
			return updatedData;
		},
		[hash],
	);

	const removeFoodLicense = useCallback(
		async (licenseId: number) => {
			const updatedData = await InsuranceLicenseService.removeFoodLicense(hash, licenseId);
			setData(updatedData);
			return updatedData;
		},
		[hash],
	);

	useEffect(() => {
		void loadData();
	}, [loadData]);

	return {
		data,
		isLoading,
		loadData,
		addLiabilityInsurance,
		removeLiabilityInsurance,
		addFoodLicense,
		removeFoodLicense,
	};
}
